"use client";

import { CenteredLoader } from "@nimble/react-requests/components/shared/loader";
import { WhenAllReady } from "@nimble/react-requests/components/shared/when-all-ready";
import { getQueryData } from "@nimble/react-requests/data/response-utils";
import { useIsMobileUnknown } from "@nimble/react-state-hooks/use-mobile";
import { InvitationItem } from "@nimble/recommendation-client/types";
import { Mail } from "lucide-react";

import InvitationChatDetailContainer from "@/app/(discover)/candidate/invites/(tabs)/invitations/components/chat-details/invitation-chat-detail";
import useInvitationsControl from "@/app/(discover)/candidate/invites/(tabs)/invitations/components/use-invitations-control";
import ChatMessageContainer from "@/app/(discover)/candidate/invites/components/chat/chat-message";
import ChatSplitView from "@/app/(discover)/candidate/invites/components/chat/chat-split-view";
import { useInvitesCacheKey } from "@/app/(discover)/candidate/invites/contexts/invites-cache-context";
import { InvitationItemState } from "@/app/(discover)/candidate/invites/lib/invites-types";
import { EmptyStateView } from "@/components/shared/empty-state";
import { useInvitations } from "@/hooks/data/use-invitations";
import { User } from "@/lib/types/auth";

function InvitationsPage({
  invitationsState,
  onClose,
  selectedInvitation,
  onRespond,
  onSelect,
}: {
  invitationsState: InvitationItemState[];
  onClose: () => void;
  onRespond: () => void;
  selectedInvitation: InvitationItemState | null;
  onSelect: (invitation: InvitationItemState) => void;
}) {
  if (invitationsState.length === 0) {
    return (
      <EmptyStateView
        title="New invitations will show up here!"
        description="When a principal is interested in your profile, they’ll send you an invitation. You’ll see it here and receive an email notification so you can review it and respond right away."
        icon={Mail}
        className="h-full"
      />
    );
  }

  const hasLockedInvitations = invitationsState.some(
    (i) => i.state === "locked",
  );

  const messages = invitationsState.map((invitationItemState) => {
    return (
      <ChatMessageContainer
        key={invitationItemState.invitation.matching_uuid}
        schoolNcesId={invitationItemState.invitation.school_nces_id}
        previewMessage={invitationItemState.invitation.school_message}
        messageState={invitationItemState.state}
        matchStatus={invitationItemState.invitation.match_status}
        onSelect={() => onSelect(invitationItemState)}
        hasLockedInvitations={hasLockedInvitations}
      />
    );
  });

  return (
    <ChatSplitView
      messages={messages}
      detailContent={
        selectedInvitation ? (
          <InvitationChatDetailContainer
            invitation={selectedInvitation.invitation}
            onClose={onClose}
            onRespond={onRespond}
          />
        ) : null
      }
    />
  );
}

export function InvitationsPageControl({
  invitations,
  isMobile,
}: {
  isMobile: boolean;
  invitations: InvitationItem[];
}) {
  const initialInvitationUuid =
    !isMobile && invitations[0] ? invitations[0].matching_uuid : null;

  const control = useInvitationsControl(
    invitations,
    initialInvitationUuid,
    isMobile,
  );

  return (
    <InvitationsPage
      invitationsState={control.invitationsState}
      onClose={control.onCloseInvitation}
      selectedInvitation={control.selectedInvitation}
      onRespond={control.onRespond}
      onSelect={control.onSelect}
    />
  );
}

export function InvitationsPageContainer({ user }: { user: User }) {
  const { invitesCacheKey } = useInvitesCacheKey();
  const invitationsQuery = useInvitations(user.id, { invitesCacheKey });
  const data = getQueryData(invitationsQuery);
  const isMobile = useIsMobileUnknown();

  return (
    <WhenAllReady queries={[invitationsQuery]} loader={<CenteredLoader />}>
      {data && isMobile !== undefined && (
        <InvitationsPageControl
          isMobile={isMobile}
          invitations={data.invitations}
        />
      )}
    </WhenAllReady>
  );
}
